import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, Eye, Clock, User, Zap, Gauge, Thermometer, Target, Wifi, Settings, Activity, ChevronUp, ChevronDown, Search, Filter, Merge, X, SendToBack } from 'lucide-react';
import { Button } from './ui/button';
import { PassViewFilters } from './ui/PassViewFilters';
import type { WeldData } from './WeldDataTable';
import { formatTimeForDisplay, formatDuration } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings, formatTimestampForDisplay } from '../utils/unitUtils';
import { getPassStatus, getStatusColorClass } from '../utils/statusUtils';
import { useDispatch, useSelector } from 'react-redux';
import { mergePasses, fetchWeldPasses, restoreMergedPasses, clearMergePassesStatus, clearRestoreMergedPassesStatus } from '../redux/WeldData/actions';
import { Checkbox } from './ui/checkbox';
import { Notification } from './ui/custom-notification';
import { Label } from './ui/label';

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #f1f1f1;
  }

  /* Smooth scrolling */
  .scrollbar-thin {
    scroll-behavior: smooth;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    .mobile-scroll-container {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
    
    .mobile-sticky-header {
      position: sticky;
      left: 0;
      background: white;
      z-index: 30;
    }
    
    .parameter-cell {
      min-width: 140px;
    }
  }
`;

const STATUS_CALCULATION_MIN_DELAY_MS = 20000;
const STATUS_CALCULATION_DELAY_PER_PASS_MS = 1000;

// Define parameter limits (in inches - will be converted based on display unit)
const PARAMETER_LIMITS = {
  voltage: { min: 20, max: 30 },
  current: { min: 150, max: 250 },
  travelSpeed: { min: 100, max: 300 },
  heat: { min: 1.0, max: 2.0 },
  trueEnergy: { min: 2.5, max: 4.5 },
  oscillationWidth: { min: 0.01, max: 10.0 },
  wireSpeed: { min: 150, max: 450 },
  totalWireConsumed: { min: 0, max: 1000 },
  distance: { min: 0, max: 1000 }
};

// Get converted parameter limits based on display unit
const getConvertedParameterLimits = () => {
  const displayUnit = getUnitFromSettings();

  if (displayUnit === 'Inches') {
    return PARAMETER_LIMITS;
  }

  // Convert limits from inches to mm for display
  return {
    voltage: PARAMETER_LIMITS.voltage,
    current: PARAMETER_LIMITS.current,
    travelSpeed: {
      min: PARAMETER_LIMITS.travelSpeed.min * 25.4,
      max: PARAMETER_LIMITS.travelSpeed.max * 25.4
    },
    heat: PARAMETER_LIMITS.heat,
    trueEnergy: PARAMETER_LIMITS.trueEnergy,
    oscillationWidth: {
      min: PARAMETER_LIMITS.oscillationWidth.min * 25.4,
      max: PARAMETER_LIMITS.oscillationWidth.max * 25.4
    },
    wireSpeed: {
      min: PARAMETER_LIMITS.wireSpeed.min * 25.4,
      max: PARAMETER_LIMITS.wireSpeed.max * 25.4
    },
    totalWireConsumed: {
      min: PARAMETER_LIMITS.totalWireConsumed.min * 25.4,
      max: PARAMETER_LIMITS.totalWireConsumed.max * 25.4
    },
    distance: {
      min: PARAMETER_LIMITS.distance.min * 25.4,
      max: PARAMETER_LIMITS.distance.max * 25.4
    }
  };
};

// Sorting types
interface Sorting {
  field: string;
  order: 'ascend' | 'descend';
}

export interface WeldPass {
  id: number;
  name: string;
  status: 'Completed' | 'Not Started' | 'In Progress' | 'Rejected';
  pass_status_by_instantaneous?: string;
  welderId: string;
  duration: string;
  startTime?: string;
  endTime?: string;
  torch?: string;
  bugType?: string;
  bugNumber?: string;
  jobNumber?: string;
  stationNumber?: string;
  autoincrement?: string;
  pipeName?: string;
  zoneNames?: string;
  pipeDiameter?: number;
  bandDiameter?: number;
  loggingDistance?: number;
  wireDiameter?: number;
  welduid_s_event_torch?: string;
  voltage?: { min: number | null; avg: number | null; max: number | null; status?: string };
  current?: { min: number | null; avg: number | null; max: number | null; status?: string };
  travelSpeed?: { min: number | null; avg: number | null; max: number | null; status?: string };
  heat?: { min: number | null; avg: number | null; max: number | null; status?: string };
  trueEnergy?: { min: number | null; avg: number | null; max: number | null; status?: string };
  oscillationWidth?: { min: number | null; avg: number | null; max: number | null; status?: string };
  wireSpeed?: { min: number | null; avg: number | null; max: number | null; status?: string };
  target?: { min: number | null; avg: number | null; max: number | null };
  horizontalBias?: { min: number | null; avg: number | null; max: number | null };
  frequency?: { min: number | null; avg: number | null; max: number | null };
  totalWireConsumed?: { min: number | null; avg: number | null; max: number | null };
  arcImpedance?: { min: number | null; avg: number | null; max: number | null };
  gps?: any;
  iwm?: any;
  conversionFactors?: any;
  // Add status fields from API response
  travel_speed_avg_status?: boolean;
  current_avg_status?: boolean;
  volts_avg_status?: boolean;
  wire_speed_avg_status?: boolean;
  oscillation_width_avg_status?: boolean;
  true_energy_avg_status?: boolean;
  heat_avg_status?: boolean;
  // Merge calculation state
  isMergeCalculating?: boolean;
  isStatusCalculating?: boolean;
  is_merged?: number;
}

interface PassViewTableProps {
  weldPasses: WeldPass[];
  passesLoading: boolean;
  passesError: string | null;
  onRowClick?: (pass: WeldPass) => void;
  statusCalculationMethod?: string;
  filters?: {
    stationNameFilter: string;
    statusFilter: string;
    bugTypeFilter: string;
    torchFilter: string;
    passFilter: string;
    zoneFilter: string;
    dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  };
  onFilterChange?: (filterType: string, value: any) => void;
  visibleColumns?: string[];
  projectId?: number | string;
  projectName?: string | null;
  weld?: WeldData;

  canEdit?: boolean;
  mergePassesEnabled?: boolean;
  setMergePassesEnabled?: (value: boolean) => void;
  showUnmergeConfirmation?: boolean;
  setShowUnmergeConfirmation?: (value: boolean) => void;
  statusCalculationStartedAt?: number | null;
}

export const PassViewTable: React.FC<PassViewTableProps> = ({
  weldPasses,
  passesLoading,
  passesError,
  onRowClick,
  statusCalculationMethod = 'Instantaneous',
  filters,
  onFilterChange,
  visibleColumns,
  projectId,
  projectName,
  weld,
  canEdit = true,
  mergePassesEnabled: mergePassesEnabledProp,
  setMergePassesEnabled: setMergePassesEnabledProp,
  showUnmergeConfirmation: showUnmergeConfirmationProp,
  setShowUnmergeConfirmation: setShowUnmergeConfirmationProp,
  statusCalculationStartedAt = null,
}) => {
  const dispatch = useDispatch();
  const mergePassesLoading = useSelector((state: any) => state.weldData?.mergePassesLoading || false);
  const mergePassesSuccess = useSelector((state: any) => state.weldData?.mergePassesSuccess || false);

  const [isMergeCalculating, setIsMergeCalculating] = useState(false);
  const [mergingPassIds, setMergingPassIds] = useState<Set<number>>(new Set());
  const [isUnmergeCalculating, setIsUnmergeCalculating] = useState(false);
  const [unmergingPassIds, setUnmergingPassIds] = useState<Set<number>>(new Set());
  const [isStatusCalculationPending, setIsStatusCalculationPending] = useState(false);
  const restoreMergedPassesSuccess = useSelector((state: any) => state.weldData?.restoreMergedPassesSuccess || false);
  const restoreMergedPassesError = useSelector((state: any) => state.weldData?.restoreMergedPassesError || null);


    // Merge passes state (use props from parent when provided, e.g. WeldPassList header)
  const [mergePassesEnabledLocal, setMergePassesEnabledLocal] = useState(false);
  const mergePassesEnabled = mergePassesEnabledProp !== undefined ? mergePassesEnabledProp : mergePassesEnabledLocal;
  const setMergePassesEnabled = setMergePassesEnabledProp ?? setMergePassesEnabledLocal;
  const [showUnmergeConfirmationLocal, setShowUnmergeConfirmationLocal] = useState(false);
  const showUnmergeConfirmation = showUnmergeConfirmationProp !== undefined ? showUnmergeConfirmationProp : showUnmergeConfirmationLocal;
  const setShowUnmergeConfirmation = setShowUnmergeConfirmationProp ?? setShowUnmergeConfirmationLocal;
  const [unmergePasses, setUnmergePasses] = useState(false);
 

  // Handle merge success - show message once, refresh data, then clear Redux so going back doesn't show loading again
  useEffect(() => {
    if (mergePassesSuccess && selectedPassForMerge) {
      const selectedIds = new Set(selectedRowsForMerge);
      setMergingPassIds(selectedIds);
      setIsMergeCalculating(true);
      setShowMergeModal(false);
      setSelectedPassForMerge(null);
      setMatchingRows([]);
      setSelectedRowsForMerge(new Set());
      Notification.success('Passes merged successfully');
      const t = setTimeout(() => {
        if (projectId && weld?.weldNumber) {
          const jobNumber = weld?.wpsNumber || null;
          dispatch(fetchWeldPasses(projectId, (projectName ?? null) as any, weld.weldNumber, jobNumber as any));
          setIsMergeCalculating(false);
          setMergingPassIds(new Set());
          dispatch(clearMergePassesStatus());
        }
      }, 25000);
      return () => clearTimeout(t);
    }
  }, [mergePassesSuccess]);

  // Handle unmerge success - show message once, refresh data, then clear Redux so going back doesn't show loading again
  useEffect(() => {
    if (restoreMergedPassesSuccess) {
      const selectedPassIds = new Set(selectedRowsForUnmerge);
      setUnmergingPassIds(selectedPassIds);
      setIsUnmergeCalculating(true);
      setShowUnmergeConfirmation(false);
      setSelectedRowsForUnmerge(new Set());
      Notification.success('Passes unmerged successfully');
      const t = setTimeout(() => {
        if (projectId && weld?.weldNumber) {
          const jobNumber = weld?.wpsNumber || null;
          dispatch(fetchWeldPasses(projectId, (projectName ?? null) as any, weld.weldNumber, jobNumber as any));
          setIsUnmergeCalculating(false);
          setUnmergingPassIds(new Set());
          dispatch(clearRestoreMergedPassesStatus());
        }
      }, 25000);
      return () => clearTimeout(t);
    }
  }, [restoreMergedPassesSuccess]);

  useEffect(() => {
    if (restoreMergedPassesError) {
      Notification.warning(
        restoreMergedPassesError === 'No merge history records found' ? 'No merge history records found' : restoreMergedPassesError,
      );
      setShowUnmergeConfirmation(false);
      setIsUnmergeCalculating(false);
      setUnmergingPassIds(new Set());
      dispatch(clearRestoreMergedPassesStatus());
    }
  }, [restoreMergedPassesError]);

  useEffect(() => {
    if (!statusCalculationStartedAt) {
      setIsStatusCalculationPending(false);
      return;
    }

    let passCount = Math.max(weldPasses.length, 1);

  
    const calculationDelay = Math.max(
      STATUS_CALCULATION_MIN_DELAY_MS,
      passCount * STATUS_CALCULATION_DELAY_PER_PASS_MS,
    );
    const remainingDelay = calculationDelay - (Date.now() - statusCalculationStartedAt);

    if (remainingDelay <= 0) {
      setIsStatusCalculationPending(false);
      return;
    }

    setIsStatusCalculationPending(true);

    const timer = setTimeout(() => {
      if (projectId && weld?.weldNumber) {
        const jobNumber = weld?.wpsNumber || null;
        dispatch(fetchWeldPasses(projectId, (projectName ?? null) as any, weld.weldNumber, jobNumber as any));
      }
      setIsStatusCalculationPending(false);
    }, remainingDelay);

    return () => clearTimeout(timer);
  }, [statusCalculationStartedAt, weldPasses.length, projectId, projectName, weld?.weldNumber, weld?.wpsNumber, dispatch]);

  // Helper function to check if a column is visible
  const isColumnVisible = useCallback((columnKey: string): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return true; // Show all columns if no visibility specified
    }
    return visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  // Format job/wps numbers: if more than 2 values, show all comma-separated
  const formatJobNumbers = useCallback((value: string | string[] | null | undefined): string => {
    if (value == null || value === '') return '-';
    const parts = Array.isArray(value)
      ? value.filter((v) => v != null && String(v).trim() !== '')
      : String(value)
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter(Boolean);
    if (parts.length === 0) return '-';
    return parts.join(', ');
  }, []);

  // Show job number column only when weld has multiple job numbers (comma/semicolon-separated)
  const jobNumberColumnVisible = useMemo(() => {
    const raw = weld?.wpsNumber;
    if (!raw || typeof raw !== 'string') return false;
    const parts = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    return parts.length > 1;
  }, [weld?.wpsNumber]);

  const isJobNumberColumnShown = isColumnVisible('job-number') && jobNumberColumnVisible;

  // Helper function to calculate sticky left position for a column
  const getStickyLeft = useCallback((columnKey: string): string => {
    const stickyColumnOrder = ['sl-no', 'status', 'job-number', 'station', 'actions'];
    const stickyColumnWidths: Record<string, number> = {
      'sl-no': 80,
      'status': 60,
      'job-number': 120,
      'station': 140,
      'actions': 100
    };
    const isStickyColVisible = (key: string) =>
      key === 'job-number' ? isJobNumberColumnShown : isColumnVisible(key);
    let left = 0;
    const currentIndex = stickyColumnOrder.indexOf(columnKey);
    for (let i = 0; i < currentIndex; i++) {
      if (isStickyColVisible(stickyColumnOrder[i])) {
        left += stickyColumnWidths[stickyColumnOrder[i]];
      }
    }
    return `${left}px`;
  }, [isColumnVisible, isJobNumberColumnShown]);

  // Local filter states for PassView specific filters
  const [passNameFilter, setPassNameFilter] = useState('');
  const [stationFilter, setStationFilter] = useState<string>('');
  const [bugTypeFilter, setBugTypeFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [welderIdFilter, setWelderIdFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });

  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Merge modal state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedPassForMerge, setSelectedPassForMerge] = useState<WeldPass | null>(null);
  const [matchingRows, setMatchingRows] = useState<WeldPass[]>([]);
  const [selectedRowsForMerge, setSelectedRowsForMerge] = useState<Set<number>>(new Set());

  // Check for mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Memoize the filter change handlers to prevent infinite re-renders
  const handleDateRangeChange = useMemo(() => (value: { dateFrom: Date | undefined; dateTo: Date | undefined }) => {
    setDateRange(value);
  }, []);

  const handlePassNameFilterChange = useMemo(() => (value: string) => {
    setPassNameFilter(value);
  }, []);

  const handleStationFilterChange = useMemo(() => (value: string) => {
    setStationFilter(value);
  }, []);

  const handleBugTypeFilterChange = useMemo(() => (value: string[]) => {
    setBugTypeFilter(value);
  }, []);

  const handleStatusFilterChange = useMemo(() => (value: string[]) => {
    setStatusFilter(value);
  }, []);

  const handleWelderIdFilterChange = useMemo(() => (value: string[]) => {
    setWelderIdFilter(value);
  }, []);

  // Sorting state
  const [sorting, setSorting] = useState<Sorting | null>(null);

  // Check if we have any M500 data based on arc impedance values
  const hasM500Data = useMemo(() => {
    return weldPasses.some((pass) => {
      const hasArcImpedance = pass.arcImpedance && (
        (pass.arcImpedance.min !== null && pass.arcImpedance.min !== undefined && pass.arcImpedance.min !== 0) ||
        (pass.arcImpedance.avg !== null && pass.arcImpedance.avg !== undefined && pass.arcImpedance.avg !== 0) ||
        (pass.arcImpedance.max !== null && pass.arcImpedance.max !== undefined && pass.arcImpedance.max !== 0)
      );
      return hasArcImpedance;
    });
  }, [weldPasses]);

  // Helper function to check if a parameter is within limits
  const isWithinLimits = (value: number, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
    const limits = getConvertedParameterLimits()[paramName];
    return value >= limits.min && value <= limits.max;
  };

  // Helper function to check if all parameters are within limits
  const areAllParamsWithinLimits = (pass: WeldPass): boolean => {
    if (pass.status !== 'Completed') return true;

    const checkParam = (param: { min: number | null; avg: number | null; max: number | null } | undefined, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
      if (!param) return true;

      // If all values are null, consider it valid (no data to validate)
      if (param.min === null && param.avg === null && param.max === null) return true;

      // Check only non-null values
      const minValid = param.min === null || isWithinLimits(param.min, paramName);
      const avgValid = param.avg === null || isWithinLimits(param.avg, paramName);
      const maxValid = param.max === null || isWithinLimits(param.max, paramName);

      return minValid && avgValid && maxValid;
    };

    return checkParam(pass.voltage, 'voltage') &&
      checkParam(pass.current, 'current') &&
      checkParam(pass.travelSpeed, 'travelSpeed') &&
      checkParam(pass.heat, 'heat') &&
      checkParam(pass.trueEnergy, 'trueEnergy') &&
      checkParam(pass.oscillationWidth, 'oscillationWidth') &&
      checkParam(pass.wireSpeed, 'wireSpeed');
  };

  // Helper function to check if a sub-column is visible
  const isSubColumnVisible = (columnKey: string, subType: 'min' | 'avg' | 'max'): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) return true;
    const subKey = `${columnKey}-${subType}`;
    return visibleColumns.includes(subKey);
  };

  // Helper function to render min/avg/max values with proper formatting
  const renderMinAvgMax = (
    values?: { min: number | null; avg: number | null; max: number | null; status?: string; avgStatus?: boolean; lowStatus?: boolean; highStatus?: boolean },
    decimals: number = 2,
    paramName?: keyof typeof PARAMETER_LIMITS,
    columnKey?: string
  ) => {
    if (!values || (values.min === null && values.avg === null && values.max === null)) {
      const visibleSubs: ('min' | 'avg' | 'max')[] = [];
      if (!columnKey || isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
      if (!columnKey || isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
      if (!columnKey || isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');

      if (visibleSubs.length === 0) return null;

      return (
        <div className="grid gap-1 h-full" style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
          {visibleSubs.map((_, idx) => (
            <div key={idx} className="text-gray-400 flex items-center justify-center h-full text-xs">-</div>
          ))}
        </div>
      );
    }

    const renderValue = (value: number | null, isAvg: boolean = false, status?: boolean) => {
      if (value === null) return '-';

      // Only convert distance-based parameters, not voltage, current, or impedance
      let displayValue;
      if (paramName && ['travelSpeed', 'wireSpeed', 'heat', 'trueEnergy', 'oscillationWidth', 'totalWireConsumed', 'distance'].includes(paramName)) {
        // Convert value from inches to display unit for distance-based parameters
        const convertedValue = convertWeldDataValue(value, paramName);
        displayValue = typeof convertedValue === 'number' ? convertedValue.toFixed(decimals) : '-';
      } else {
        // Keep original value for voltage, current, impedance, etc.
        displayValue = value.toFixed(decimals);
      }

      // Apply red background if status is false, otherwise keep normal styling
      const bgColor = status === false ? 'bg-red-100' : 'bg-white';
      const textColor = status === false ? 'text-red-800' : (isAvg ? 'text-gray-900' : 'text-blue-600');

      return (
        <div className={`flex items-center justify-center h-full w-full ${bgColor} ${textColor} rounded-lg px-1 font-medium text-xs break-all`}>
          {displayValue}
        </div>
      );
    };

    // Determine which sub-columns are visible
    const visibleSubs: Array<{ type: 'min' | 'avg' | 'max', value: number | null, status?: boolean }> = [];
    if (!columnKey || isSubColumnVisible(columnKey, 'min')) {
      visibleSubs.push({ type: 'min', value: values.min, status: values.lowStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'avg')) {
      visibleSubs.push({ type: 'avg', value: values.avg, status: values.avgStatus });
    }
    if (!columnKey || isSubColumnVisible(columnKey, 'max')) {
      visibleSubs.push({ type: 'max', value: values.max, status: values.highStatus });
    }

    if (visibleSubs.length === 0) return null;

    // Dynamic grid class based on number of visible sub-columns
    const gridColsClass = visibleSubs.length === 1 ? 'grid-cols-1' : visibleSubs.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

    return (
      <div className={`grid ${gridColsClass} gap-1 rounded-md h-full bg-gray-50 min-w-0`} style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
        {visibleSubs.map((sub) => (
          <div key={sub.type} className="flex items-center justify-center h-full min-w-0">
            <div className="flex items-center justify-center h-full w-full min-w-0">
              {renderValue(sub.value, sub.type === 'avg', sub.status)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to render header labels based on visible sub-columns
  const renderHeaderLabels = (columnKey: string) => {
    const visibleSubs: ('min' | 'avg' | 'max')[] = [];
    if (isSubColumnVisible(columnKey, 'min')) visibleSubs.push('min');
    if (isSubColumnVisible(columnKey, 'avg')) visibleSubs.push('avg');
    if (isSubColumnVisible(columnKey, 'max')) visibleSubs.push('max');

    if (visibleSubs.length === 0) return null;

    const labels: Record<'min' | 'avg' | 'max', string> = { min: 'Min', avg: 'Avg', max: 'Max' };
    const gridColsClass = visibleSubs.length === 1 ? 'grid-cols-1' : visibleSubs.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

    return (
      <div className={`grid ${gridColsClass} text-xs font-normal text-center text-gray-500`} style={{ gridTemplateColumns: `repeat(${visibleSubs.length}, minmax(0, 1fr))` }}>
        {visibleSubs.map(sub => (
          <div key={sub}>{labels[sub]}</div>
        ))}
      </div>
    );
  };

  // Sorting function
  const handleSort = (field: string) => {
    if (sorting?.field === field) {
      setSorting(sorting.order === 'ascend' ? { field, order: 'descend' } : null);
    } else {
      setSorting({ field, order: 'ascend' });
    }
  };

  // Render sort icon
  const renderSortIcon = (field: string) => {
    if (sorting?.field !== field) {
      return <ChevronUp className="h-3 w-3 text-gray-400" />;
    }
    return sorting.order === 'ascend' ?
      <ChevronUp className="h-3 w-3 text-blue-600" /> :
      <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  // Helper function to check if a pass has matching rows
  const hasMatchingRows = (pass: WeldPass): boolean => {
    const matching = filteredWeldPasses.filter((p: WeldPass) => {
      return (
        p.stationNumber === pass.stationNumber &&
        p.bugType === pass.bugType &&
        p.torch === pass.torch &&
        p.name === pass.name &&
        p.id !== pass.id // Exclude the clicked row itself
      );
    });
    return matching.length > 0;
  };

  // Merge functionality
  const handleMergeClick = (pass: WeldPass) => {
    // Find matching rows based on station, bugType, torch, and pass name
    const matching = filteredWeldPasses.filter((p: WeldPass) => {
      return (
        p.stationNumber === pass.stationNumber &&
        p.bugType === pass.bugType &&
        p.torch === pass.torch &&
        p.name === pass.name &&
        p.id !== pass.id // Exclude the clicked row itself
      );
    });

    // Combine clicked row with matching rows
    const allRows = [pass, ...matching];

    // Sort by start time (earliest first)
    const sortedRows = allRows.sort((a, b) => {
      const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
      const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
      return aTime - bTime; // Ascending order (earliest first)
    });

    // Find the row with the earliest start time (first in sorted array)
    const earliestRow = sortedRows[0];

    setSelectedPassForMerge(earliestRow);
    setMatchingRows(sortedRows); // Set sorted rows by start time
    setSelectedRowsForMerge(new Set([earliestRow.id])); // Pre-select the row with earliest start time
    setShowMergeModal(true);
  };

  const handleRowCheckboxChange = (rowId: number, checked: boolean) => {
    const newSelected = new Set(selectedRowsForMerge);
    if (checked as boolean) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRowsForMerge(newSelected);
  };

  const handleConfirmMerge = () => {
    if (selectedRowsForMerge.size < 2) return;

    // Filter and sort only the rows the user selected
    let rowsToMerge = matchingRows
      .filter((row: WeldPass) => selectedRowsForMerge.has(row.id));

    rowsToMerge.sort((a, b) => {
      const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
      const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
      return aTime - bTime; // Ascending order (earliest first)
    });

    if (rowsToMerge.length < 2) return;

    // Get the earliest selected row's welduid_s_event_torch value
    const primaryRow = rowsToMerge[0];
    const welduidSEventTorch = primaryRow.welduid_s_event_torch || '';

    // Remaining rows contribute to the merge list
    const welduidSEventTorchList = rowsToMerge
      .slice(1)
      .map((row: WeldPass) => row.welduid_s_event_torch)
      .filter((welduid): welduid is string => Boolean(welduid));

    // Check if project_id is available
    if (!projectId) {
      console.error('Project ID is not available');
      return;
    }

    if (!weld?.weldNumber) {
      console.error('Weld Number is not available');
      return;
    }
    if (!weld?.wpsNumber) {
      console.error('Job Number is not available');
      return;
    }

    // Prepare payload for API
    const payload = {
      welduid_s_event_torch_list: welduidSEventTorchList,
      project_id: projectId,
      welduid_s_event_torch: welduidSEventTorch,
      weldNumber: weld?.weldNumber || '',
      jobNumber: weld?.wpsNumber || '',
    };

    // Dispatch the merge action
    dispatch(mergePasses(payload) as any);
    // Modal will close and data will refresh on success via useEffect
  };

  // Get dynamic unit labels for table headers
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Helper function to render converted min/max values for headers (only for distance-based parameters)
  const renderHeaderLimits = (paramName: keyof typeof PARAMETER_LIMITS) => {
    const limits = getConvertedParameterLimits()[paramName];
    const minValue = limits.min.toFixed(0);
    const maxValue = limits.max.toFixed(0);

    return (
      <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
        <div>{minValue}</div>
        <div>avg</div>
        <div>{maxValue}</div>
      </div>
    );
  };

  // Helper function to render static min/avg/max labels for non-distance parameters
  const renderStaticHeaderLabels = () => {
    return (
      <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
        <div>Min</div>
        <div>Avg</div>
        <div>Max</div>
      </div>
    );
  };

  // Extract unique options for dropdowns
  const uniqueOptions = useMemo(() => {
    const bugTypes = Array.from(new Set(weldPasses.map(pass => pass.bugType).filter((type): type is string => Boolean(type))));
    const statuses = Array.from(new Set(weldPasses.map(pass => pass.status))) as string[];
    const welderIds = Array.from(new Set(weldPasses.map(pass => pass.welderId).filter((id): id is string => Boolean(id))));
    const stations = Array.from(new Set(weldPasses.map(pass => pass.stationNumber).filter((station): station is string => Boolean(station))));

    return {
      bugTypes,
      statuses,
      welderIds,
      stations
    };
  }, [weldPasses]);

  // Apply filters to data
  const filteredWeldPasses = useMemo(() => {
    let filtered = weldPasses.filter((pass: WeldPass) => {
      // Pass name filter
      if (passNameFilter && pass.name) {
        const nameMatch = pass.name.toLowerCase().includes(passNameFilter.toLowerCase());
        if (!nameMatch) return false;
      }

      // Station filter
      if (stationFilter && pass.stationNumber && !String(pass.stationNumber).toLowerCase().includes(stationFilter.toLowerCase())) {
        return false;
      }

      // Bug type filter
      if (bugTypeFilter.length > 0 && pass.bugType && !bugTypeFilter.includes(pass.bugType)) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const status = getPassStatus(statusCalculationMethod, pass);
        if (status && !statusFilter.includes(status)) {
          return false;
        }
      }

      // Welder ID filter
      if (welderIdFilter.length > 0 && pass.welderId && !welderIdFilter.includes(pass.welderId)) {
        return false;
      }

      // Date range filter - check both start and end times
      if (dateRange.dateFrom || dateRange.dateTo) {
        const passStartDate = pass.startTime ? new Date(pass.startTime) : null;
        const passEndDate = pass.endTime ? new Date(pass.endTime) : null;

        // If we have a date range filter, at least one of the pass dates should fall within the range
        let hasDateInRange = false;

        if (passStartDate) {
          const startDate = new Date(passStartDate);
          startDate.setHours(0, 0, 0, 0);

          if (dateRange.dateFrom) {
            const fromDate = new Date(dateRange.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (startDate >= fromDate) {
              if (!dateRange.dateTo || startDate <= new Date(dateRange.dateTo)) {
                hasDateInRange = true;
              }
            }
          } else if (dateRange.dateTo) {
            const toDate = new Date(dateRange.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (startDate <= toDate) {
              hasDateInRange = true;
            }
          }
        }

        if (!hasDateInRange && passEndDate) {
          const endDate = new Date(passEndDate);
          endDate.setHours(0, 0, 0, 0);

          if (dateRange.dateFrom) {
            const fromDate = new Date(dateRange.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (endDate >= fromDate) {
              if (!dateRange.dateTo || endDate <= new Date(dateRange.dateTo)) {
                hasDateInRange = true;
              }
            }
          } else if (dateRange.dateTo) {
            const toDate = new Date(dateRange.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (endDate <= toDate) {
              hasDateInRange = true;
            }
          }
        }

        if (!hasDateInRange) {
          return false;
        }
      }

      return true;
    });
    // if(!isMergeCalculating) {
    //   filtered = filtered.filter((pass: WeldPass) => pass.is_merged !== 1);
    // }
    // else if(!isUnmergeCalculating) {
    //   filtered = filtered.filter((pass: WeldPass) => pass.is_merged !== 2);
    // }
    // Show only rows with is_merged !== 2 in the main table
   filtered = filtered.filter((pass: WeldPass) => pass.is_merged !== 2);

   // console.log('filtered', filtered);
    // Apply sorting
    if (sorting) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sorting.field) {
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'bugType':
            aValue = a.bugType || '';
            bValue = b.bugType || '';
            break;
          case 'torch':
            aValue = a.torch || '';
            bValue = b.torch || '';
            break;
          case 'welderId':
            aValue = a.welderId || '';
            bValue = b.welderId || '';
            break;
          case 'startTime':
            aValue = a.startTime ? new Date(a.startTime).getTime() : 0;
            bValue = b.startTime ? new Date(b.startTime).getTime() : 0;
            break;
          case 'endTime':
            aValue = a.endTime ? new Date(a.endTime).getTime() : 0;
            bValue = b.endTime ? new Date(b.endTime).getTime() : 0;
            break;
          case 'duration':
            aValue = a.duration || '';
            bValue = b.duration || '';
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sorting.order === 'ascend' ? -1 : 1;
        if (aValue > bValue) return sorting.order === 'ascend' ? 1 : -1;
        return 0;
      });
    }

    if (isMergeCalculating && mergingPassIds.size > 0) {
      filtered = filtered.map((pass: WeldPass) => {
        if (mergingPassIds.has(pass.id)) {
          return {
            ...pass,
            isMergeCalculating: isMergeCalculating
          }
        }
        return pass;
      });
    }
    else if (isUnmergeCalculating && unmergingPassIds.size > 0) {
      filtered = filtered.map((pass: WeldPass) => {
        if (unmergingPassIds.has(pass.id)) {
          return {
            ...pass,
            isMergeCalculating: isUnmergeCalculating
          }
        }
        return pass;
      });
    }

    if (isStatusCalculationPending) {
      filtered = filtered.map((pass: WeldPass) => ({
        ...pass,
        isStatusCalculating: true
      }));
    }

    return filtered;
  }, [weldPasses, passNameFilter, stationFilter, bugTypeFilter, statusFilter, welderIdFilter, dateRange, sorting, statusCalculationMethod, isMergeCalculating, mergingPassIds, isUnmergeCalculating, unmergingPassIds, isStatusCalculationPending]);

  // Inject custom scrollbar styles
  useEffect(() => {
    const styleId = 'pass-view-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = scrollbarStyles;
      document.head.appendChild(style);
    }

    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, []);

  const [selectedRowsForUnmerge, setSelectedRowsForUnmerge] = useState<Set<number>>(new Set());
  const restoreMergedPassesLoading = useSelector((state: any) => state.weldData?.restoreMergedPassesLoading || false);

  // Passes with is_merged === 1 (hidden from main table) for Un Merge modal
  const mergedPassesForUnmerge = useMemo(
    () => weldPasses.filter((pass: WeldPass) => pass.is_merged === 1),
    [weldPasses]
  );

  const handleUnmergeRowCheckboxChange = (rowId: number, checked: boolean) => {
    const newSelected = new Set(selectedRowsForUnmerge);
    if (checked) {
      newSelected.add(rowId);
    } else {
      newSelected.delete(rowId);
    }
    setSelectedRowsForUnmerge(newSelected);
  };

  const handleConfirmUnmerge = () => {
    if (!projectId || !weld?.weldNumber || !weld?.wpsNumber) {
      setShowUnmergeConfirmation(false);
      return;
    }
    const selectedPasses = mergedPassesForUnmerge.filter((p) => selectedRowsForUnmerge.has(p.id));
    const welduid_s_event_torch_list = selectedPasses
      .map((p) => p.welduid_s_event_torch)
      .filter((w): w is string => Boolean(w));
    if (welduid_s_event_torch_list.length === 0) return;

    const payload = {
      project_id: projectId,
      weldNumber: weld.weldNumber,
      jobNumber: weld.wpsNumber,
      welduid_s_event_torch_list,
    };
    dispatch(restoreMergedPasses(payload) as any);
  };

  // Mobile table view - simplified for small screens
  const renderMobileTable = () => (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Mobile Filter Toggle */}
      <div className="flex-shrink-0 p-2 border-b">
        <Button
          variant="outline"
          size="sm"
          className="w-full flex items-center justify-center gap-2"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {filteredWeldPasses.length !== weldPasses.length && (
            <span className="text-blueprint-blue bg-[rgba(0,39,63,0.06)] text-xs px-2 py-1 rounded-full">
              {filteredWeldPasses.length}/{weldPasses.length}
            </span>
          )}
        </Button>
      </div>

      {/* Mobile Filters */}
      {showFilters && (
        <div className="flex-shrink-0 p-2 border-b bg-gray-50">
          <PassViewFilters
            passNameFilter={passNameFilter}
            onPassNameFilterChange={handlePassNameFilterChange}
            stationFilter={stationFilter}
            onStationFilterChange={handleStationFilterChange}
            bugTypeFilter={bugTypeFilter}
            onBugTypeFilterChange={handleBugTypeFilterChange}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            welderIdFilter={welderIdFilter}
            onWelderIdFilterChange={handleWelderIdFilterChange}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            bugTypeOptions={uniqueOptions.bugTypes}
            statusOptions={uniqueOptions.statuses}
            welderIdOptions={uniqueOptions.welderIds}
            stationOptions={uniqueOptions.stations}
            totalCount={weldPasses.length}
            filteredCount={filteredWeldPasses.length}
            isMobile={true}
          />
        </div>
      )}

      {/* Mobile Table Content */}
      <div className="flex-1 overflow-x-auto overflow-y-auto mobile-scroll-container" style={{ WebkitOverflowScrolling: 'touch' }}>
        {passesLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <span className="text-gray-600 text-lg">Loading weld passes...</span>
            </div>
          </div>
        ) : passesError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-600 text-center p-4">
              <p>Error loading weld passes:</p>
              <p className="text-sm">{passesError}</p>
            </div>
          </div>
        ) : filteredWeldPasses.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 text-center p-4">
              <p>No weld passes available for this weld.</p>
              {passNameFilter || bugTypeFilter.length > 0 || statusFilter.length > 0 || welderIdFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo ? (
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              ) : null}
            </div>
          </div>
        ) : (
          <div style={{ minWidth: '800px' }}>
            {/* Fixed Header for Mobile */}
            <div className="sticky top-0 z-20 bg-white border-b">
              <div className="grid grid-cols-12 gap-1 p-2 text-xs font-medium text-gray-600 bg-gray-50">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-2 text-center">Station</div>
                <div className="col-span-2 text-center">Pass</div>
                <div className="col-span-2 text-center">Time</div>
                <div className="col-span-2 text-center">Start</div>
                <div className="col-span-2 text-center">Action</div>
              </div>
            </div>

            {/* Mobile Table Rows */}
            <div className="divide-y">
              {filteredWeldPasses.map((pass: WeldPass, index: number) => (
                <div
                  key={pass.id}
                  className="p-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => onRowClick?.(pass)}
                >
                  <div className="grid grid-cols-12 gap-1 items-center text-xs">
                    <div className="col-span-1 text-center text-gray-600 font-medium">
                      {index + 1}
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {(() => {
                        if (pass.isMergeCalculating || pass.isStatusCalculating) {
                          return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
                        }
                        const status = getPassStatus(statusCalculationMethod, pass);
                        if (status === 'TRUE') {
                          return <ThumbsUp className="h-4 w-4 text-green-600" />;
                        } else if (status === 'FALSE') {
                          return <ThumbsDown className="h-4 w-4 text-red-600" />;
                        } else if (status === 'EX') {
                          return <span className="h-4 w-4 flex items-center justify-center font-bold text-xs bg-yellow-400 text-black rounded-full">!</span>;
                        } else if (status === 'CROSS') {
                          return <span className="h-4 w-4 flex items-center justify-center font-bold text-xs text-red-600">×</span>;
                        } else if (status === 'NULL' || status === null || status === undefined) {
                          return <span className="text-gray-600">-</span>;
                        } else {
                          return <div className="h-4 w-4 rounded-full bg-yellow-400 animate-pulse" />;
                        }
                      })()}
                    </div>
                    <div className="col-span-2 text-center text-gray-600 truncate">
                      {pass.stationNumber ? `S${pass.stationNumber}` : '-'}
                    </div>
                    <div className="col-span-2 text-center font-medium truncate">
                      {pass.name}
                    </div>
                    <div className="col-span-2 text-center text-gray-600">
                      {formatDuration(pass.duration)}
                    </div>
                    <div className="col-span-2 text-center text-gray-600 text-xs truncate">
                      {pass.startTime ? formatTimeForDisplay(pass.startTime) : '-'}
                    </div>
                    <div className="col-span-2 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="hover:bg-gray-100 p-1"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Additional info for mobile */}
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500">
                    <div className="truncate">
                      <span className="font-medium">Bug:</span> {pass.bugType || '-'}
                    </div>
                    <div className="truncate">
                      <span className="font-medium">Torch:</span> {pass.torch || '-'}
                    </div>
                  </div>

                  {/* Key parameters summary for mobile */}
                  <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                    <div className="text-center">
                      <div className="font-medium text-gray-600">Current (A)</div>
                      <div className="text-blue-600">
                        {pass.current?.avg?.toFixed(0) || '-'}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-600">Voltage (V)</div>
                      <div className="text-blue-600">
                        {pass.voltage?.avg?.toFixed(1) || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Desktop table view - full featured with horizontal scrolling
  const renderDesktopTable = () => (
    <div className="h-full flex flex-col w-full overflow-hidden">
      {/* Desktop Filters */}
      <div className="flex-shrink-0 px-2 sm:px-4">
        <PassViewFilters
          passNameFilter={passNameFilter}
          onPassNameFilterChange={handlePassNameFilterChange}
          stationFilter={stationFilter}
          onStationFilterChange={handleStationFilterChange}
          bugTypeFilter={bugTypeFilter}
          onBugTypeFilterChange={handleBugTypeFilterChange}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          welderIdFilter={welderIdFilter}
          onWelderIdFilterChange={handleWelderIdFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          bugTypeOptions={uniqueOptions.bugTypes}
          statusOptions={uniqueOptions.statuses}
          welderIdOptions={uniqueOptions.welderIds}
          stationOptions={uniqueOptions.stations}
          totalCount={weldPasses.length}
          filteredCount={filteredWeldPasses.length}
        />
      </div>
      {/* Desktop Table with Global Horizontal Scroll */}
      <div className="flex-1 relative overflow-hidden">
        {passesLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
              <span className="text-gray-600 text-lg">Loading weld passes...</span>
            </div>
          </div>
        ) : passesError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-600 text-center">
              <p>Error loading weld passes:</p>
              <p className="text-sm">{passesError}</p>
            </div>
          </div>
        ) : filteredWeldPasses.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 text-center">
              <p>No weld passes available for this weld.</p>
              {passNameFilter || bugTypeFilter.length > 0 || statusFilter.length > 0 || welderIdFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo ? (
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              ) : null}
            </div>
          </div>
        ) : (
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
            style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorX: 'contain'
            }}
          >
            {/* Single unified table with sticky columns */}
            <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
              <thead className="sticky top-0 bg-white z-30">
                <tr className="border-b h-[72px] bg-gray-50">
                  {/* Sticky columns */}
                  {isColumnVisible('sl-no') && (
                    <th className="sticky left-0 z-20 bg-gray-50 min-w-[60px] w-[80px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center justify-center gap-1">
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Sl.no</span>
                        <span className="sm:hidden">#</span>
                      </div>
                    </th>
                  )}
                  {isColumnVisible('status') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[60px] w-[60px] max-w-[60px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ width: '60px', left: getStickyLeft('status') }}>
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Status</span>
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  )}
                  {isJobNumberColumnShown && (
                    <th className="sticky z-20 bg-gray-50 min-w-[100px] w-[120px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('job-number') }}>
                      Job Number
                    </th>
                  )}
                  {isColumnVisible('station') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[100px] w-[120px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{ left: getStickyLeft('station') }}>
                      <button
                        onClick={() => handleSort('stationNumber')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        Station
                        {renderSortIcon('stationNumber')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('actions') && (
                    <th className="sticky z-20 bg-gray-50 min-w-[80px] w-[80px] sm:w-[100px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600" style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                      <div className="flex items-center justify-center gap-1">
                        <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                        Actions
                      </div>
                    </th>
                  )}

                  {/* Scrollable columns */}
                  {isColumnVisible('welder-id') && (
                    <th className="min-w-[100px] w-[120px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('welderId')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <User className="h-3 w-3 sm:h-4 sm:w-4" />
                        Welder ID
                        {renderSortIcon('welderId')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('bug-type') && (
                    <th className="min-w-[100px] w-[120px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('bugType')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Settings className="h-3 w-3 sm:h-4 sm:w-4" />
                        Bug Type
                        {renderSortIcon('bugType')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('torch') && (
                    <th className="min-w-[80px] w-[100px] sm:w-[140px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('torch')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <User className="h-3 w-3 sm:h-4 sm:w-4" />
                        Torch
                        {renderSortIcon('torch')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('pass-name') && (
                    <th className="min-w-[120px] w-[140px] sm:w-[160px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        Pass Name
                        {renderSortIcon('name')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('weld-start-time') && (
                    <th className="min-w-[140px] w-[140px] sm:w-[160px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('startTime')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Weld Start Time</span>
                        <span className="sm:hidden">Start</span>
                        {renderSortIcon('startTime')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('weld-time') && (
                    <th className="min-w-[70px] w-[70px] sm:w-[80px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('duration')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Gauge className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Weld Time</span>
                        <span className="sm:hidden">Time</span>
                        {renderSortIcon('duration')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('auto-increment') && (
                    <th className="min-w-[120px] w-[140px] sm:w-[160px] px-2 sm:px-4 text-center text-xs font-medium text-gray-600 border-r">
                      <button
                        onClick={() => handleSort('autoincrement')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="hidden sm:inline">Auto Increment</span>
                        <span className="sm:hidden">Auto Inc</span>
                        {renderSortIcon('autoincrement')}
                      </button>
                    </th>
                  )}

                  {/* Parameter columns */}
                  {isColumnVisible('current') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          Current<br />(A)
                        </div>
                      </div>
                      {renderHeaderLabels('current')}
                    </th>
                  )}
                  {isColumnVisible('voltage') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          Voltage<br />(V)
                        </div>
                      </div>
                      {renderHeaderLabels('voltage')}
                    </th>
                  )}
                  {isColumnVisible('travel-speed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Travel Speed<br />({getUnitLabel('travel_speed')})
                        </div>
                      </div>
                      {renderHeaderLabels('travel-speed')}
                    </th>
                  )}
                  {isColumnVisible('true-energy') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          True Energy<br />({getUnitLabel('true_energy')})
                        </div>
                      </div>
                      {renderHeaderLabels('true-energy')}
                    </th>
                  )}
                  {isColumnVisible('heat') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Thermometer className="h-4 w-4" />
                          Heat<br />({getUnitLabel('heat')})
                        </div>
                      </div>
                      {renderHeaderLabels('heat')}
                    </th>
                  )}
                  {isColumnVisible('wire-speed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Wire Speed<br />({getUnitLabel('wire_speed')})
                        </div>
                      </div>
                      {renderHeaderLabels('wire-speed')}
                    </th>
                  )}
                  {isColumnVisible('oscillation-width') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Wifi className="h-4 w-4" />
                          Oscillation Width<br />({getUnitLabel('oscillation_width')})
                        </div>
                      </div>
                      {renderHeaderLabels('oscillation-width')}
                    </th>
                  )}
                  {isColumnVisible('frequency') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Activity className="h-4 w-4" />
                          Frequency<br />({getUnitLabel('frequency')})
                        </div>
                      </div>
                      {renderHeaderLabels('frequency')}
                    </th>
                  )}
                  {isColumnVisible('target') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Target className="h-4 w-4" />
                          Target<br />({getUnitLabel('target')})
                        </div>
                      </div>
                      {renderHeaderLabels('target')}
                    </th>
                  )}
                  {isColumnVisible('horizontal-bias') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Horizontal Bias<br />({getUnitLabel('horizontal_bias')})
                        </div>
                      </div>
                      {renderHeaderLabels('horizontal-bias')}
                    </th>
                  )}
                  {isColumnVisible('total-wire-consumed') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-4 w-4" />
                          Total Wire Consumed<br />({getUnitLabel('total_wire_consumed')})
                        </div>
                      </div>
                      {renderHeaderLabels('total-wire-consumed')}
                    </th>
                  )}
                  {hasM500Data && isColumnVisible('arc-impedance') && (
                    <th className="px-2 min-w-[150px] max-w-[250px] w-auto border-l border-gray-200">
                      <div className="h-[40px] flex items-center justify-center text-center font-medium text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Zap className="h-4 w-4" />
                          Arc Impedance<br />(mOhm)
                        </div>
                      </div>
                      {renderHeaderLabels('arc-impedance')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredWeldPasses.map((pass: WeldPass, index: number) => (
                  <tr
                    key={pass.id}
                    className={`h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${index < filteredWeldPasses.length - 1 ? 'border-b' : ''}`}
                  >
                    {/* Sticky columns */}
                    {isColumnVisible('sl-no') && (
                      <td className={`sticky z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-2 sm:px-4 text-center text-gray-600 text-xs font-medium shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('sl-no') }}>{index + 1}</td>
                    )}
                    {isColumnVisible('status') && (
                      <td className={`sticky z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} min-w-[60px] w-[60px] max-w-[60px] px-2 sm:px-4 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ width: '60px', left: getStickyLeft('status') }}>
                        <div className="inline-flex justify-center">
                          {(() => {
                            if (pass.isMergeCalculating || pass.isStatusCalculating) {
                              return <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 animate-spin" />;
                            }
                            const status = getPassStatus(statusCalculationMethod, pass);
                            if (status === 'TRUE') {
                              return <ThumbsUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />;
                            } else if (status === 'FALSE') {
                              return <ThumbsDown className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />;
                            } else if (status === 'EX') {
                              return <span className="h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center font-bold text-xs sm:text-sm bg-yellow-400 text-black rounded-full">!</span>;
                            } else if (status === 'CROSS') {
                              return <span className="h-4 w-4 sm:h-5 sm:w-5 flex items-center justify-center font-bold text-xs sm:text-sm text-red-600">×</span>;
                            } else if (status === 'NULL' || status === null || status === undefined) {
                              return <span className="text-gray-600">-</span>;
                            } else {
                              return <div className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-yellow-400 animate-pulse" />;
                            }
                          })()}
                        </div>
                      </td>
                    )}
                      {isJobNumberColumnShown && (
                      <td className={`sticky z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-2 sm:px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('job-number') }}>{formatJobNumbers(pass.jobNumber ?? weld?.wpsNumber)}</td>
                    )}
                    {isColumnVisible('station') && (
                      <td className={`sticky z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-2 sm:px-4 text-center text-gray-600 text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`} style={{ left: getStickyLeft('station') }}>{pass.stationNumber ? `Station ${pass.stationNumber}` : '-'}</td>
                    )}
                    {isColumnVisible('actions') && (
                      <td className={`sticky z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} px-2 sm:px-4 text-center`} style={{ left: getStickyLeft('actions'), boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hover:bg-gray-100 p-1 sm:p-2"
                            onClick={() => onRowClick?.(pass)}
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          {mergePassesEnabled && canEdit && (() => {
                            const passStatus = getPassStatus(statusCalculationMethod, pass);
                           const isdisabled = passStatus === 'NULL' || passStatus == null || passStatus === 'EX';
                            return (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-gray-100 p-1 sm:p-2"
                              onClick={() => handleMergeClick(pass)}
                              disabled={!hasMatchingRows(pass) || isdisabled}
                              title={!hasMatchingRows(pass) ? "No matching rows found" : "Merge matching rows"}
                            >
                              <Merge className={`h-3 w-3 sm:h-4 sm:w-4 ${!hasMatchingRows(pass) ? 'opacity-50' : ''}`} />
                            </Button>
                            );
                          })()}
                        </div>
                      </td>
                    )}

                    {/* Scrollable columns */}
                    {isColumnVisible('welder-id') && (
                      <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{pass.welderId || '-'}</td>
                    )}
                    {isColumnVisible('bug-type') && (
                      <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{pass.bugType || '-'}</td>
                    )}
                    {isColumnVisible('torch') && (
                      <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{pass.torch || '-'}</td>
                    )}
                    {isColumnVisible('pass-name') && (
                      <td className="px-2 sm:px-4 text-center font-medium text-xs">{pass.name}</td>
                    )}
                    {isColumnVisible('weld-start-time') && (
                      <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{pass.startTime ? formatTimeForDisplay(pass.startTime) : '-'}</td>
                    )}
                    {isColumnVisible('weld-time') && (
                      <td className="px-2 sm:px-4 text-center text-gray-600 text-xs">{formatDuration(pass.duration)}</td>
                    )}
                    {isColumnVisible('auto-increment') && (
                      <td className="px-2 sm:px-4 text-center text-gray-600 text-xs border-r">{pass.autoincrement || '-'}</td>
                    )}

                    {/* Parameter columns */}
                    {isColumnVisible('current') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.current, 0, 'current', 'current')}</td>
                    )}
                    {isColumnVisible('voltage') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.voltage, 2, 'voltage', 'voltage')}</td>
                    )}
                    {isColumnVisible('travel-speed') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.travelSpeed, 1, 'travelSpeed', 'travel-speed')}</td>
                    )}
                    {isColumnVisible('true-energy') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.trueEnergy, 2, 'trueEnergy', 'true-energy')}</td>
                    )}
                    {isColumnVisible('heat') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.heat, 2, 'heat', 'heat')}</td>
                    )}
                    {isColumnVisible('wire-speed') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.wireSpeed, 0, 'wireSpeed', 'wire-speed')}</td>
                    )}
                    {isColumnVisible('oscillation-width') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.oscillationWidth, 2, 'oscillationWidth', 'oscillation-width')}</td>
                    )}
                    {isColumnVisible('frequency') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.frequency, 0, undefined, 'frequency')}</td>
                    )}
                    {isColumnVisible('target') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.target, 0, undefined, 'target')}</td>
                    )}
                    {isColumnVisible('horizontal-bias') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.horizontalBias, 0, undefined, 'horizontal-bias')}</td>
                    )}
                    {isColumnVisible('total-wire-consumed') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.totalWireConsumed, 0, 'totalWireConsumed', 'total-wire-consumed')}</td>
                    )}
                    {hasM500Data && isColumnVisible('arc-impedance') && (
                      <td className="px-2 py-2 h-[52px] text-xs min-w-0 w-32 parameter-cell">{renderMinAvgMax(pass.arcImpedance, 0, undefined, 'arc-impedance')}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // Main render - conditionally show mobile or desktop view
  return (
    <div className="h-full w-full">
      {/* Unmerge Modal - table of merged passes (is_merged === 1) with checkboxes */}
      {showUnmergeConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Un Merge Passes</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowUnmergeConfirmation(false);
                  setSelectedRowsForUnmerge(new Set());
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mb-4 text-sm text-gray-600">
              <p className="mt-1">Select merged passes to unmerge. Selected: {selectedRowsForUnmerge.size} row(s)</p>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 w-[50px]">
                      <Checkbox
                        aria-label="Select all rows"
                        checked={mergedPassesForUnmerge.length > 0 && mergedPassesForUnmerge.every((row) => selectedRowsForUnmerge.has(row.id))}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRowsForUnmerge(new Set(mergedPassesForUnmerge.map((r) => r.id)));
                          } else {
                            setSelectedRowsForUnmerge(new Set());
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Sl.no</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Station</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Bug Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Torch</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Pass Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Weld Start Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Weld Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Auto Increment</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedPassesForUnmerge.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                        No merged passes  found
                      </td>
                    </tr>
                  ) : (
                    mergedPassesForUnmerge.map((pass: WeldPass, index: number) => {
                      const isSelected = selectedRowsForUnmerge.has(pass.id);
                      return (
                        <tr
                          key={pass.id}
                          className={`border-b ${isSelected ? 'bg-blue-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                        >
                          <td className="px-4 py-2">
                            <Checkbox
                              aria-label={`Select row ${index + 1} for unmerge`}
                              checked={isSelected}
                              onCheckedChange={(checked) => handleUnmergeRowCheckboxChange(pass.id, checked === true)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-600">{index + 1}</td>
                          <td className="px-4 py-2 text-xs">
                            <div className="inline-flex justify-center">
                              {(() => {
                                const status = getPassStatus(statusCalculationMethod, pass);
                                if (status === 'TRUE') {
                                  return <ThumbsUp className="h-4 w-4 text-green-600" />;
                                } else if (status === 'FALSE') {
                                  return <ThumbsDown className="h-4 w-4 text-red-600" />;
                                } else if (status === 'EX') {
                                  return <span className="h-4 w-4 flex items-center justify-center font-bold text-xs bg-yellow-400 text-black rounded-full">!</span>;
                                } else if (status === 'CROSS') {
                                  return <span className="h-4 w-4 flex items-center justify-center font-bold text-xs text-red-600">×</span>;
                                } else if (status === 'NULL' || status === null || status === undefined) {
                                  return <span className="text-gray-600">-</span>;
                                } else {
                                  return <div className="h-4 w-4 rounded-full bg-yellow-400 animate-pulse" />;
                                }
                              })()}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.stationNumber ? `Station ${pass.stationNumber}` : '-'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.bugType || '-'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.torch || '-'}</td>
                          <td className="px-4 py-2 text-xs font-medium">{pass.name}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.startTime ? formatTimeForDisplay(pass.startTime) : '-'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{formatDuration(pass.duration)}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.autoincrement || '-'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowUnmergeConfirmation(false);
                  setSelectedRowsForUnmerge(new Set());
                }}
                disabled={restoreMergedPassesLoading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleConfirmUnmerge}
                disabled={selectedRowsForUnmerge.size === 0 || restoreMergedPassesLoading}
                className="bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white"
              >
                {restoreMergedPassesLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Unmerging...
                  </>
                ) : (
                  `Un Merge Selected (${selectedRowsForUnmerge.size})`
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Merge Passes</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowMergeModal(false);
                  setSelectedPassForMerge(null);
                  setMatchingRows([]);
                  setSelectedRowsForMerge(new Set());
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="mb-4 text-sm text-gray-600">
              {/* <p>Matching rows by: Station, Bug Type, Torch, Pass Name</p> */}
              <p className="mt-1">Selected: {selectedRowsForMerge.size} row(s)</p>
            </div>

            <div className="flex-1 overflow-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 w-[50px]">
                      <Checkbox
                        aria-label="Select all rows"
                        checked={matchingRows.length > 0 && matchingRows.every(row => selectedRowsForMerge.has(row.id))}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedRowsForMerge(new Set(matchingRows.map(r => r.id)));
                          } else {
                            setSelectedRowsForMerge(new Set());
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Sl.no</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Station</th>

                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Bug Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Torch</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Pass Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Weld Start Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Weld Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Auto Increment</th>
                  </tr>
                </thead>
                <tbody>
                  {matchingRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                        No matching rows found
                      </td>
                    </tr>
                  ) : (
                    matchingRows.map((pass: WeldPass, index: number) => {
                      const isSelected = selectedRowsForMerge.has(pass.id);
                      return (
                        <tr
                          key={pass.id}
                          className={`border-b ${isSelected ? 'bg-blue-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}
                        >
                          <td className="px-4 py-2">
                            <Checkbox
                              aria-label={`Select row ${index + 1} for merging`}
                              checked={isSelected}
                              onCheckedChange={(checked) => handleRowCheckboxChange(pass.id, checked as boolean)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-600">{index + 1}</td>
                          <td className="px-4 py-2 text-xs">
                            <div className="inline-flex justify-center">
                              {(() => {
                                const status = getPassStatus(statusCalculationMethod, pass);
                                if (status === 'TRUE') {
                                  return <ThumbsUp className="h-4 w-4 text-green-600" />;
                                } else if (status === 'FALSE') {
                                  return <ThumbsDown className="h-4 w-4 text-red-600" />;
                                } else if (status === 'EX') {
                                  return <span className="h-4 w-4 flex items-center justify-center font-bold text-xs bg-yellow-400 text-black rounded-full">!</span>;
                                } else if (status === 'CROSS') {
                                  return <span className="h-4 w-4 flex items-center justify-center font-bold text-xs text-red-600">×</span>;
                                } else if (status === 'NULL' || status === null || status === undefined) {
                                  return <span className="text-gray-600">-</span>;
                                } else {
                                  return <div className="h-4 w-4 rounded-full bg-yellow-400 animate-pulse" />;
                                }
                              })()}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.stationNumber ? `Station ${pass.stationNumber}` : '-'}</td>

                          <td className="px-4 py-2 text-xs text-gray-600">{pass.bugType || '-'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.torch || '-'}</td>
                          <td className="px-4 py-2 text-xs font-medium">{pass.name}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.startTime ? formatTimeForDisplay(pass.startTime) : '-'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{formatDuration(pass.duration)}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{pass.autoincrement || '-'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowMergeModal(false);
                  setSelectedPassForMerge(null);
                  setMatchingRows([]);
                  setSelectedRowsForMerge(new Set());
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleConfirmMerge}
                disabled={selectedRowsForMerge.size < 2 || mergePassesLoading}
              >
                {mergePassesLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Merging...
                  </>
                ) : (
                  `Merge Selected (${selectedRowsForMerge.size})`
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isMobile ? renderMobileTable() : renderDesktopTable()}
    </div>
  );
};